import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import html2canvas from 'html2canvas';
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  Check,
  LocateFixed,
  LoaderCircle,
  MessageCircle,
  ScanLine,
  Trash2,
  UploadCloud,
  X
} from 'lucide-react';
import { enrichLocation, getPublicSettings, requestSignedUpload, submitSurvey, uploadFileToSignedUrl } from './api.js';
import { inferUploadContentType, isImageUpload } from './fileTypes.js';
import { bytesToSize, getDraftSession, resetDraftSession } from './utils.js';
import AdminApp from './AdminApp.jsx';

const steps = [
  { id: 'awb', title: 'Scan AWB', subtitle: 'Scan AWB IDExpress' },
  { id: 'recipientName', title: 'Nama Penerima', subtitle: 'Masukan nama penerima paket', required: true },
  { id: 'recipientRelation', title: 'Siapa Penerima', required: true },
  { id: 'recipientPhotos', title: 'Foto Penerima', subtitle: 'Foto penerima + Produk', required: true },
  { id: 'documentPhotos', title: 'Foto Dokumen', subtitle: 'KTP / SIM (jika menggunakan SIM perlu ada Notif Grab)', required: true },
  { id: 'doResiPhotos', title: 'Foto DO + RESI AJC', subtitle: 'Foto DO + Foto HAWB AJC', required: true },
  {
    id: 'additionalPhotos',
    title: 'Foto Tambahan',
    subtitle: 'KK (jika penerima pengganti). Notifikasi Grab terkait pengiriman tersebut (jika penerima menggunakan SIM)'
  },
  {
    id: 'finish',
    title: 'SELESAI!',
    subtitle: 'Catat atau cek galery untuk bukti pengiriman. Jangan lupa SUBMIT!',
    required: true
  }
];

const uploadFields = ['recipientPhotos', 'documentPhotos', 'doResiPhotos', 'additionalPhotos'];

const emptyUploads = {
  recipientPhotos: [],
  documentPhotos: [],
  doResiPhotos: [],
  additionalPhotos: []
};

function App() {
  if (window.location.pathname.startsWith('/backoffice')) return <AdminApp />;

  return <SurveyApp />;
}

function SurveyApp() {
  const session = useMemo(() => getDraftSession(), []);
  const appShellRef = useRef(null);
  const proofCaptureDoneRef = useRef(false);
  const [publicSettings, setPublicSettings] = useState({ status: 'loading', data: null, error: '' });
  const [started, setStarted] = useState(false);
  const [locationState, setLocationState] = useState({ status: 'idle', payload: null, error: '' });
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [cameraTarget, setCameraTarget] = useState(null);
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });
  const [confirmationError, setConfirmationError] = useState('');
  const [formData, setFormData] = useState({
    awb: '',
    recipientName: '',
    recipientRelation: '',
    proofCode: session.proofCode
  });
  const [uploads, setUploads] = useState(emptyUploads);

  const currentStep = steps[stepIndex];

  useEffect(() => {
    getPublicSettings()
      .then((data) => setPublicSettings({ status: 'ready', data, error: '' }))
      .catch((error) => setPublicSettings({ status: 'failed', data: null, error: error.message }));
  }, []);

  const downloadProofCapture = useCallback(async () => {
    const element = appShellRef.current;
    if (!element) return false;

    try {
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        useCORS: true,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        onclone: (clonedDocument) => {
          clonedDocument.querySelector('.app-shell')?.classList.add('capture-mode');
        }
      });
      const imageUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `pod-proof-${formData.awb || session.proofCode}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (error) {
      setSubmitState((current) => ({
        ...current,
        message: current.message || `Gagal membuat screenshot otomatis: ${error.message}`
      }));
      return false;
    }
  }, [formData.awb, session.proofCode]);

  useEffect(() => {
    if (currentStep.id !== 'finish' || proofCaptureDoneRef.current) return;
    proofCaptureDoneRef.current = true;
    const timer = window.setTimeout(() => {
      downloadProofCapture();
    }, 450);
    return () => window.clearTimeout(timer);
  }, [currentStep.id, downloadProofCapture]);

  function buildConfirmationText() {
    const settings = publicSettings.data || {};
    const projectName = settings.activeProject?.name || 'No Project';
    const template = settings.confirmationMessageTemplate || 'TF99eNxTBZc2\\nkonfirmasi#<project>#<proofCode>';
    return template
      .replace(/\\n|\/n/g, '\n')
      .replace(/<project>|\{\{project\}\}|\{project\}/g, projectName)
      .replace(/<proofCode>|\{\{proofCode\}\}|\{proofCode\}|#proofCode/g, formData.proofCode)
      .replace(/<awb>|\{\{awb\}\}|\{awb\}|#awb/g, formData.awb.trim());
  }

  function normalizeWhatsAppPhone(value) {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.startsWith('0')) return `62${digits.slice(1)}`;
    return digits;
  }

  function openConfirmationWhatsApp() {
    if (!publicSettings.data?.confirmationEnabled) return false;
    const phone = normalizeWhatsAppPhone(publicSettings.data?.confirmationPhone);
    if (!phone) {
      setConfirmationError('Nomor konfirmasi belum diset di Firestore.');
      return false;
    }
    setConfirmationError('');
    const message = buildConfirmationText();
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    return true;
  }

  async function captureLocationAndStart() {
    if (!publicSettings.data?.submissionEnabled) return;
    setLocationState({ status: 'requesting', payload: null, error: '' });

    if (!navigator.geolocation) {
      setLocationState({ status: 'blocked', payload: null, error: 'Browser tidak mendukung geolocation.' });
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0
        });
      });

      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        heading: position.coords.heading,
        speed: position.coords.speed
      };
      const payload = await enrichLocation(coords);
      setLocationState({ status: 'ready', payload, error: '' });
      setStarted(true);
    } catch (error) {
      setLocationState({
        status: 'blocked',
        payload: null,
        error: error.message || 'Lokasi wajib diizinkan sebelum mulai.'
      });
    }
  }

  function setField(field, value) {
    setStepError('');
    setFormData((current) => ({ ...current, [field]: value }));
  }

  function uploadedDone(category) {
    return uploads[category].filter((file) => file.status === 'done');
  }

  function uploadBusy(category) {
    return uploads[category].some((file) => file.status === 'uploading');
  }

  function validateStep(index = stepIndex) {
    const step = steps[index];
    if (step.id === 'awb' && !formData.awb.trim()) return 'AWB wajib discan atau diisi.';
    if (step.id === 'recipientName' && !formData.recipientName.trim()) return 'Nama penerima wajib diisi.';
    if (step.id === 'recipientRelation' && !formData.recipientRelation) return 'Pilih siapa penerima paket.';
    if (['recipientPhotos', 'documentPhotos', 'doResiPhotos'].includes(step.id)) {
      if (uploadBusy(step.id)) return 'Tunggu upload foto selesai.';
      if (uploadedDone(step.id).length < 1) return 'Minimal satu foto wajib diupload.';
    }
    if (step.id === 'additionalPhotos' && uploadBusy(step.id)) return 'Tunggu upload foto selesai.';
    return '';
  }

  function goNext() {
    const error = validateStep();
    if (error) {
      setStepError(error);
      return;
    }
    setStepError('');
    setStepIndex((index) => Math.min(index + 1, steps.length - 1));
  }

  function goPrevious() {
    setStepError('');
    setStepIndex((index) => Math.max(index - 1, 0));
  }

  async function addFiles(category, fileList) {
    const incoming = [...fileList].filter(isImageUpload);
    if (!incoming.length) return;

    const currentCount = uploads[category].length;
    const availableSlots = Math.max(0, 5 - currentCount);
    const accepted = incoming.slice(0, availableSlots);
    if (!accepted.length) {
      setStepError('Maksimal 5 foto untuk bagian ini.');
      return;
    }
    if (incoming.length > accepted.length) {
      setStepError('Sebagian foto tidak ditambahkan karena batas maksimal 5.');
    } else {
      setStepError('');
    }

    for (const file of accepted) {
      const contentType = inferUploadContentType(file);
      const localId = `${category}-${crypto.randomUUID?.() || Date.now()}-${file.name}`;
      const item = {
        id: localId,
        name: file.name,
        size: file.size,
        type: contentType,
        previewUrl: URL.createObjectURL(file),
        status: 'uploading',
        error: ''
      };

      setUploads((current) => ({
        ...current,
        [category]: [...current[category], item]
      }));

      try {
        const signed = await requestSignedUpload({
          idempotencyKey: session.idempotencyKey,
          category,
          file
        });
        await uploadFileToSignedUrl({ uploadUrl: signed.uploadUrl, file, contentType: signed.contentType || contentType });
        setUploads((current) => ({
          ...current,
          [category]: current[category].map((existing) =>
            existing.id === localId
              ? {
                  ...existing,
                  status: 'done',
                  objectName: signed.objectName,
                  gcsUri: signed.gcsUri
                }
              : existing
          )
        }));
      } catch (error) {
        setUploads((current) => ({
          ...current,
          [category]: current[category].map((existing) =>
            existing.id === localId ? { ...existing, status: 'failed', error: error.message } : existing
          )
        }));
      }
    }
  }

  function removeUpload(category, id) {
    setUploads((current) => {
      const removed = current[category].find((file) => file.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return {
        ...current,
        [category]: current[category].filter((file) => file.id !== id)
      };
    });
  }

  function filesForPayload() {
    return Object.fromEntries(
      uploadFields.map((field) => [
        field,
        uploads[field]
          .filter((file) => file.status === 'done')
          .map((file) => ({
            fileName: file.name,
            contentType: file.type,
            size: file.size,
            objectName: file.objectName,
            gcsUri: file.gcsUri
          }))
      ])
    );
  }

  async function handleSubmit() {
    for (let index = 0; index < steps.length - 1; index += 1) {
      const error = validateStep(index);
      if (error) {
        setStepIndex(index);
        setStepError(error);
        return;
      }
    }

    if (!locationState.payload) {
      setStarted(false);
      setLocationState({ status: 'blocked', payload: null, error: 'Lokasi wajib ditangkap ulang.' });
      return;
    }

    setSubmitState({ status: 'submitting', message: '' });
    try {
      await submitSurvey({
        idempotencyKey: session.idempotencyKey,
        formData: {
          awb: formData.awb.trim(),
          recipientName: formData.recipientName.trim(),
          recipientRelation: formData.recipientRelation,
          proofCode: formData.proofCode
        },
        files: filesForPayload(),
        location: locationState.payload,
        clientMeta: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          screen: `${window.screen.width}x${window.screen.height}`
        }
      });

      setSubmitState({
        status: 'submitted',
        message: publicSettings.data?.confirmationEnabled
          ? 'Data tersimpan, resi sudah disimpan di galery, pastikan kirimkan konfirmasi dengan menekan tombol dibawah'
          : 'Data tersimpan, resi sudah disimpan di galery.'
      });
      openConfirmationWhatsApp();
      resetDraftSession(session.storageKey);
    } catch (error) {
      setSubmitState({ status: 'failed', message: error.message });
    }
  }

  return (
    <main className="app-shell" ref={appShellRef}>
      <div className="brand-mark" aria-label="IDExpress">
        <span>iD</span>
        <strong>EXPRESS</strong>
      </div>

      {publicSettings.status === 'loading' ? (
        <NoSurveyCard title="Loading" message="Checking survey availability." />
      ) : publicSettings.status === 'failed' ? (
        <NoSurveyCard title="No Survey" message={publicSettings.error || 'Survey is unavailable.'} />
      ) : !publicSettings.data?.submissionEnabled ? (
        <NoSurveyCard title="No Survey" message="Survey sedang tidak tersedia." />
      ) : !started ? (
        <WelcomeCard locationState={locationState} settings={publicSettings.data} onStart={captureLocationAndStart} />
      ) : (
        <>
          <SurveyCard
            step={currentStep}
            stepIndex={stepIndex}
            formData={formData}
            uploads={uploads}
            stepError={stepError}
            submitState={submitState}
            confirmationError={confirmationError}
            confirmationEnabled={publicSettings.data?.confirmationEnabled}
            onFieldChange={setField}
            onScan={() => setScannerOpen(true)}
            onFiles={addFiles}
            onCamera={setCameraTarget}
            onRemoveFile={removeUpload}
            onPrevious={goPrevious}
            onNext={goNext}
            onSubmit={handleSubmit}
            onDownloadProof={downloadProofCapture}
            onConfirm={openConfirmationWhatsApp}
          />
          <ProgressDots current={stepIndex} total={steps.length} />
        </>
      )}

      {scannerOpen && (
        <BarcodeScanner
          onClose={() => setScannerOpen(false)}
          onResult={(value) => {
            setField('awb', value);
            setScannerOpen(false);
          }}
        />
      )}

      {cameraTarget && (
        <CameraCapture
          onClose={() => setCameraTarget(null)}
          onCapture={(file) => {
            addFiles(cameraTarget, [file]);
            setCameraTarget(null);
          }}
        />
      )}
    </main>
  );
}

function WelcomeCard({ locationState, settings, onStart }) {
  const requesting = locationState.status === 'requesting';
  return (
    <section className="welcome-card">
      <div className="welcome-content">
        <LocateFixed className="welcome-icon" size={34} aria-hidden="true" />
        <h1>Welcome</h1>
        <p>Hi there, please fill out and submit this form.</p>
        <small className="project-pill">{settings?.activeProject?.name || 'No Project'}</small>
        <span>8 Questions</span>
        {locationState.error && <div className="inline-error">{locationState.error}</div>}
      </div>
      <button className="primary-strip" type="button" onClick={onStart} disabled={requesting}>
        {requesting ? (
          <>
            <LoaderCircle className="spin" size={22} />
            DETECTING LOCATION
          </>
        ) : (
          <>
            START
            <ArrowRight size={24} />
          </>
        )}
      </button>
    </section>
  );
}

function NoSurveyCard({ title, message }) {
  return (
    <section className="welcome-card no-survey-card">
      <div className="welcome-content">
        <h1>{title}</h1>
        <p>{message}</p>
      </div>
    </section>
  );
}

function SurveyCard(props) {
  const { step, stepIndex, onPrevious, onNext, onSubmit, submitState } = props;
  const isLast = step.id === 'finish';

  return (
    <section className="survey-card">
      <div className="card-body">
        <h2 className={props.stepError ? 'has-error' : ''}>
          {step.title}
          {step.required && <sup>*</sup>}
        </h2>
        {step.subtitle && <p className="subtitle">{step.subtitle}</p>}
        <StepBody {...props} />
        {props.stepError && <div className="inline-error">{props.stepError}</div>}
        {submitState.message && <div className={submitState.status === 'failed' ? 'inline-error' : 'inline-success'}>{submitState.message}</div>}
      </div>

      <div className="card-actions">
        <button type="button" className="nav-btn previous" onClick={onPrevious} disabled={stepIndex === 0 || submitState.status === 'submitting'}>
          <ArrowLeft size={22} />
          PREVIOUS
        </button>
        {isLast ? (
          <button type="button" className="nav-btn next" onClick={onSubmit} disabled={submitState.status === 'submitting' || submitState.status === 'submitted'}>
            {submitState.status === 'submitting' ? <LoaderCircle className="spin" size={20} /> : null}
            SUBMIT
          </button>
        ) : (
          <button type="button" className="nav-btn next" onClick={onNext}>
            NEXT
            <ArrowRight size={22} />
          </button>
        )}
      </div>
    </section>
  );
}

function StepBody({
  step,
  formData,
  uploads,
  submitState,
  confirmationError,
  confirmationEnabled,
  onFieldChange,
  onScan,
  onFiles,
  onCamera,
  onRemoveFile,
  onDownloadProof,
  onConfirm
}) {
  if (step.id === 'awb') {
    return (
      <div className="scan-box">
        <button className="scan-button" type="button" onClick={onScan}>
          <ScanLine size={19} />
          SCAN BARCODE
        </button>
        <input
          className="text-input awb-input"
          value={formData.awb}
          onChange={(event) => onFieldChange('awb', event.target.value)}
          placeholder="Hasil scan AWB"
          autoComplete="off"
        />
      </div>
    );
  }

  if (step.id === 'recipientName') {
    return (
      <input
        className="text-input"
        value={formData.recipientName}
        onChange={(event) => onFieldChange('recipientName', event.target.value)}
        autoComplete="name"
      />
    );
  }

  if (step.id === 'recipientRelation') {
    return (
      <select
        className="select-input"
        value={formData.recipientRelation}
        onChange={(event) => onFieldChange('recipientRelation', event.target.value)}
      >
        <option value="">Please Select</option>
        <option value="Penerima Asli">Penerima Asli</option>
        <option value="Keluarga Inti">Keluarga Inti</option>
      </select>
    );
  }

  if (uploadFields.includes(step.id)) {
    return (
      <UploadField
        category={step.id}
        files={uploads[step.id]}
        onFiles={onFiles}
        onCamera={onCamera}
        onRemove={onRemoveFile}
      />
    );
  }

  return (
    <div className="proof-stack">
      <input className="text-input proof-input" value={formData.proofCode} readOnly aria-label="Random proof code" />
      <button className="download-proof-button" type="button" onClick={onDownloadProof}>
        DOWNLOAD BUKTI
      </button>
      {confirmationEnabled && submitState.status === 'submitted' && (
        <button className="confirm-button" type="button" onClick={onConfirm}>
          <MessageCircle size={19} />
          KONFIRMASI
        </button>
      )}
      {confirmationError && <div className="inline-error">{confirmationError}</div>}
    </div>
  );
}

function UploadField({ category, files, onFiles, onCamera, onRemove }) {
  const inputRef = useRef(null);

  function handleDrop(event) {
    event.preventDefault();
    onFiles(category, event.dataTransfer.files);
  }

  return (
    <div className="upload-wrapper">
      <div className="upload-zone" onDragOver={(event) => event.preventDefault()} onDrop={handleDrop}>
        <UploadCloud size={32} />
        <span>Drag and drop files here</span>
        <div className="upload-actions">
          <button type="button" className="secondary-button camera-button" onClick={() => onCamera(category)} disabled={files.length >= 5}>
            <Camera size={18} />
            CAMERA
          </button>
          <button type="button" className="secondary-button" onClick={() => inputRef.current?.click()} disabled={files.length >= 5}>
            BROWSE FILES
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.heic,.heif,image/heic,image/heif"
          multiple
          hidden
          onChange={(event) => {
            onFiles(category, event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      <div className="file-grid">
        {files.map((file) => (
          <div className={`file-item ${file.status}`} key={file.id}>
            <img src={file.previewUrl} alt={file.name} />
            <div className="file-meta">
              <strong>{file.name}</strong>
              <span>{bytesToSize(file.size)}</span>
              {file.status === 'uploading' && <span>Uploading...</span>}
              {file.status === 'done' && <span className="done-label"><Check size={14} /> Uploaded</span>}
              {file.status === 'failed' && <span className="failed-label">{file.error}</span>}
            </div>
            <button type="button" className="icon-button" onClick={() => onRemove(category, file.id)} aria-label={`Remove ${file.name}`}>
              <Trash2 size={17} />
            </button>
          </div>
        ))}
      </div>
      <div className="limit-label">{files.length} of 5 photos</div>
    </div>
  );
}

function ProgressDots({ current, total }) {
  return (
    <div className="progress-wrap" aria-label={`Step ${current + 1} of ${total}`}>
      <div className="progress-line" />
      <div className="dots">
        {Array.from({ length: total }, (_, index) => (
          <span key={index} className={index === current ? 'active' : index < current ? 'completed' : ''} />
        ))}
      </div>
      <strong>{current + 1} of {total}</strong>
    </div>
  );
}

function BarcodeScanner({ onClose, onResult }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        const reader = new BrowserMultiFormatReader();
        const controls = await reader.decodeFromConstraints(
          {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            },
            audio: false
          },
          videoRef.current,
          (result) => {
            if (result) {
              controlsRef.current?.stop();
              onResult(result.getText());
            }
          }
        );
        if (cancelled) {
          controls.stop();
        } else {
          controlsRef.current = controls;
        }
      } catch (scanError) {
        setError(scanError.message || 'Kamera tidak bisa dibuka.');
      }
    }

    start();
    return () => {
      cancelled = true;
      controlsRef.current?.stop();
    };
  }, [onResult]);

  return (
    <Modal title="Scan AWB" onClose={onClose}>
      <div className="video-frame">
        <video ref={videoRef} muted playsInline autoPlay />
        <div className="scan-reticle" />
      </div>
      {error && <div className="inline-error">{error}</div>}
    </Modal>
  );
}

function CameraCapture({ onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 4096 },
            height: { ideal: 2160 },
            aspectRatio: { ideal: 4 / 3 }
          },
          audio: false
        });

        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        videoRef.current.srcObject = stream;
      } catch (cameraError) {
        setError(cameraError.message || 'Kamera tidak bisa dibuka.');
      }
    }

    startCamera();
    return () => {
      mounted = false;
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  async function capture() {
    const video = videoRef.current;
    if (!video?.videoWidth) {
      setError('Kamera belum siap.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Gagal mengambil foto.');
          return;
        }
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
      },
      'image/jpeg',
      0.92
    );
  }

  return (
    <Modal title="Ambil Foto" onClose={onClose}>
      <div className="video-frame camera-frame">
        <video ref={videoRef} muted playsInline autoPlay />
      </div>
      {error && <div className="inline-error">{error}</div>}
      <button className="capture-button" type="button" onClick={capture}>
        <Camera size={20} />
        CAPTURE
      </button>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-panel">
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default App;
